// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useMemo, type ReactNode } from 'react';

import type {
  FacilityId,
  PartCatalogId,
  PartId,
  QrCodeId,
  ShipmentId,
  ShopRole,
  TaskId,
  WorkOrderId,
} from '@evolvemep/api-ts';
import { Navigate, Outlet, Route, Routes, useOutletContext } from 'react-router';

import { DesktopLayoutCard } from 'app/Layout/DesktopLayoutCard';
import { MobileUnsupportedGate } from 'app/Layout/MobileUnsupportedGate';
import { moduleRoute } from 'app/Layout/NavBar/globalNavigationMenu';
import { useCanAccessRoute } from 'app/Layout/NavBar/NavBarContent/useCanAccessRoute';
import { useMessages } from 'app/MessageCenter/MessagesContext';
import { PageNotFound } from 'app/PageNotFound';
import { hasLengthAtLeast } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';
import { strongEntries, strongKeys } from 'helpers/strongEntries';
import { useIsMobile } from 'hooks/useIsMobile';
import type { AccessArea, AccessLevel } from 'hooks/user/useHasAccess';
import { useUser } from 'hooks/user/useUser';
import { CompanyMembersPage } from 'modules/Admin/CompanyMembers/CompanyMembersPage';
import { CompanyPropertiesPage } from 'modules/Admin/CompanyProperties/CompanyPropertiesPage';
import { ContentRequestPage } from 'modules/Admin/ContentRequest/ContentRequestPage';
import { FacilitiesPage } from 'modules/Admin/Facilities/FacilitiesPage';
import { LicensesPage } from 'modules/Admin/Licenses/LicensesMantine/LicensesPage';
import type { ProjectPropertiesTab } from 'modules/Admin/Projects/ProjectProperties/ProjectDetailsPane';
import { ProjectsPage } from 'modules/Admin/Projects/ProjectsPage';
import { BillOfMaterialsPage } from 'modules/Shop/BillOfMaterials/BillOfMaterialsPage';
import { CatalogSetupPage } from 'modules/Shop/CatalogSetup/CatalogSetupPage';
import { PartEditorPage } from 'modules/Shop/CatalogSetup/PartEditor/PartEditorPage';
import type { MaterialAnalyticsChartId } from 'modules/Shop/Insights/MaterialAnalytics/MaterialAnalyticsLandingPage';
import { AnalyticsLandingPage, type ChartRoute } from 'modules/Shop/Insights/RevitUsage/AnalyticsLandingPage';
import { InsightsPage } from 'modules/Shop/Insights/ShopInsights/InsightsPage';
import { TaskDataPage } from 'modules/Shop/Insights/TaskData/TaskDataPage';
import { ModelBrowserPage } from 'modules/Shop/ModelBrowser/ModelBrowserPage';
import { ShipmentDetailPage } from 'modules/Shop/Shipments/ShipmentDetail/ShipmentDetailPage';
import { ShipmentsPage } from 'modules/Shop/Shipments/ShipmentsPage/ShipmentsPage';
import { WorkCellSetupPage } from 'modules/Shop/ShopSetup/WorkCellSetup/ShopSetupPage/WorkCellSetupPage';
import { SpoolingPage } from 'modules/Shop/Spooling/SpoolingPage';
import { TaskViewerPage } from 'modules/Shop/Tasks/TaskViewer/TaskViewerPage';
import { WorkCellTasksPage } from 'modules/Shop/Tasks/WorkCellTasksPage';
import { TimeTrackingPage } from 'modules/Shop/TimeTracking/TimeTrackingPage';
import { MobileWorkOrderActivityView } from 'modules/Shop/WorkOrders/WorkOrderDetail/Mobile/MobileWorkOrderActivityView';
import { MobileWorkOrderAttachmentsView } from 'modules/Shop/WorkOrders/WorkOrderDetail/Mobile/MobileWorkOrderAttachmentsView';
import { MobileWorkOrderDetailPage } from 'modules/Shop/WorkOrders/WorkOrderDetail/Mobile/MobileWorkOrderDetailPage';
import { MobileWorkOrderDetailsView } from 'modules/Shop/WorkOrders/WorkOrderDetail/Mobile/MobileWorkOrderDetailsView';
import {
  WorkOrderDetailPage,
  WorkOrderDetailWrapper,
} from 'modules/Shop/WorkOrders/WorkOrderDetail/WorkOrderDetailPage';
import { MobileWorkOrdersPage } from 'modules/Shop/WorkOrders/WorkOrdersPage/Mobile/MobileWorkOrdersPage';
import { WorkOrdersPage } from 'modules/Shop/WorkOrders/WorkOrdersPage/WorkOrdersPage';

import { RouteBasedWelcomeModal } from './WelcomeModals/RouteBasedWelcomeModal';

//region Lazy imported components
const CustomersPage = React.lazy(() => import('modules/Management/customers/CustomersPage'));
const ProductFeaturesPage = React.lazy(() => import('modules/Management/ProductFeatures/ProductFeaturesPage'));
const RevitDownloadPage = React.lazy(() => import('modules/Management/RevitDownloads/RevitDownloadPage'));
const EvolveFabricationPage = React.lazy(() => import('modules/Shop/EvolveFabrication/EvolveFabricationPage'));
const MaterialAnalyticsLandingPage = React.lazy(
  () => import('modules/Shop/Insights/MaterialAnalytics/MaterialAnalyticsLandingPage'),
);
const MaterialAnalyticsChartPage = React.lazy(
  () => import('modules/Shop/Insights/MaterialAnalytics/MaterialAnalyticsChartPage'),
);
//endregion

//region Mobile route helpers

/** A mobile route override that renders a single page, with no sub-pages. */
type SimpleMobileRouteConfig<TContext extends object> =
  | {
      /**
       * Rendered instead of the route's desktop `Component` on mobile viewports.
       * Without a `Layout`, nothing provides context — so the page must not require props.
       */
      Component: React.FC;
      Layout?: never;
      subRoutes?: never;
    }
  | {
      /**
       * Rendered instead of the route's desktop `Component` on mobile viewports.
       * Receives the layout's context as props.
       */
      Component: React.FC<TContext>;
      /**
       * Provides the page's data: load it once, then call `children(context)`. Routing is handled
       * by `ModuleRoutes` — layouts don't need to know react-router exists.
       */
      Layout: React.FC<{ children: (context: TContext) => ReactNode }>;
      subRoutes?: never;
    };

/** A mobile route override with sub-pages sharing data loaded once by `Layout`. */
type SubRoutedMobileRouteConfig<
  TContext extends object,
  TSubRoutes extends Record<`/${string}`, React.FC<TContext>>,
> = {
  /**
   * Rendered instead of the route's desktop `Component` on mobile viewports, as the index route beneath `Layout`.
   * Receives the layout's context as props.
   */
  Component: React.FC<TContext>;
  /**
   * Provides the data shared by the mobile page and its sub-pages: load it once, then call
   * `children(context)`. Routing (the `<Outlet />` bridge) is handled by `ModuleRoutes` —
   * layouts don't need to know react-router exists.
   */
  Layout: React.FC<{ children: (context: TContext) => ReactNode }>;
  /**
   * Mobile-only sub-pages beneath `Layout`, keyed by path (e.g. `/details`). Each receives the
   * layout's context as props. The keys also extend `ShopNavigationRoute`, making
   * `${route}${subRoute}` URLs navigable — declared here once, derived everywhere else.
   */
  subRoutes: TSubRoutes;
};

/** Mobile overrides for a route in `ALL_MODULE_ROUTES`. */
type MobileRouteConfig<TContext extends object> =
  | SimpleMobileRouteConfig<TContext>
  | SubRoutedMobileRouteConfig<TContext, Record<`/${string}`, React.FC<TContext>>>;

// The widened view of a `mobile` config used by the render glue;
// each entry's real `TContext` was already checked at its `mobileRoute` call
// oxlint-disable-next-line typescript/no-explicit-any
type AnyMobileRouteConfig = MobileRouteConfig<any>;

/**
 * Ties a mobile route's pieces together type-wise: when a `Layout` is present, `TContext` is
 * inferred from its render prop, and `Component` plus every `subRoutes` page must accept exactly
 * that context as props — a mismatch is a compile error here at the config. Without a `Layout`,
 * `Component` must not require props (nothing would provide them). The sub-routed overload
 * preserves the literal `subRoutes` keys so navigable sub-route URLs derive from the config (see
 * `WithMobileSubRoutes`); the simple shapes stamp `subRoutes?: never` so no config is a structural
 * subtype of another (which would let TypeScript's union subtype reduction silently drop the
 * richer config from the entries' value union).
 */
function mobileRoute<TContext extends object, TSubRoutes extends Record<`/${string}`, React.FC<TContext>>>(
  config: SubRoutedMobileRouteConfig<TContext, TSubRoutes>,
): SubRoutedMobileRouteConfig<TContext, TSubRoutes>;
function mobileRoute<TContext extends object>(
  config: SimpleMobileRouteConfig<TContext>,
): SimpleMobileRouteConfig<TContext>;
function mobileRoute(config: object): object {
  return config;
}

/**
 * Bridges a mobile sub-paged route's page into react-router:
 * reads the context its `Layout` published via `<Outlet context>` and passes it to the page as props.
 */
const OutletContextPage = <TContext extends object>({ Page }: { Page: React.FC<TContext> }) => {
  const context = useOutletContext<TContext>();
  return <Page {...context} />;
};

//endregion

/** All Shop routes. */
export const ALL_MODULE_ROUTES = {
  // Management (EVOLVE ONLY)
  '/evolve-customers': {
    Component: CustomersPage,
    accessArea: 'marketing',
    accessRequired: 'write',
  },
  '/revit-downloads': {
    Component: RevitDownloadPage,
    accessArea: 'evolveSettings',
    accessRequired: 'write',
  },
  '/product-features': {
    Component: ProductFeaturesPage,
    accessArea: 'evolveSettings',
    accessRequired: 'write',
  },

  // Insights
  '/analytics': {
    Component: AnalyticsLandingPage,
    accessArea: 'insights',
    accessRequired: 'read',
  },
  '/materials-analytics': {
    Component: MaterialAnalyticsLandingPage,
    accessArea: 'materialAnalytics',
    accessRequired: 'read',
  },
  '/materials-analytics/:chartId': {
    Component: MaterialAnalyticsChartPage,
    accessArea: 'materialAnalytics',
    accessRequired: 'read',
  },
  '/analytics/:chartId': {
    Component: AnalyticsLandingPage,
    accessArea: 'insights',
    accessRequired: 'read',
  },
  '/insights': {
    Component: InsightsPage,
    accessArea: 'shopInsights',
    accessRequired: 'read',
  },
  '/task-data': {
    Component: TaskDataPage,
    accessArea: 'shopInsights',
    accessRequired: 'read',
  },

  // Admin
  '/content-request': {
    Component: ContentRequestPage,
    accessArea: 'companySettings',
    accessRequired: 'write',
  },
  '/company-members': {
    Component: CompanyMembersPage,
    accessArea: 'companyMembers',
    accessRequired: 'read',
  },
  '/shop-properties': {
    Component: CompanyPropertiesPage,
    accessArea: 'shopSettings',
    accessRequired: 'write',
  },
  '/licenses': {
    Component: LicensesPage,
    accessArea: 'companySettings',
    accessRequired: 'write',
  },
  '/facilities': {
    Component: FacilitiesPage,
    accessArea: 'facilities',
    accessRequired: 'write',
  },
  '/projects': {
    Component: ProjectsPage,
    accessArea: 'projects',
    accessRequired: 'write',
  },
  '/projects/:tab': {
    Component: ProjectsPage,
    accessArea: 'projects',
    accessRequired: 'write',
  },

  // Shop Setup
  '/work-cell-setup/:facilityId': {
    Component: WorkCellSetupPage,
    accessArea: 'workCellSetup',
    accessRequired: 'write',
  },
  '/catalog-setup': {
    Component: CatalogSetupPage,
    accessArea: 'catalog',
    accessRequired: 'write',
  },
  '/add-catalog-item/:partCatalogId': {
    Component: PartEditorPage,
    accessArea: 'catalog',
    accessRequired: 'write',
  },
  '/edit-catalog-item/:partId': {
    Component: PartEditorPage,
    accessArea: 'catalog',
    accessRequired: 'write',
  },

  // Work Planning
  '/model-browser': {
    Component: ModelBrowserPage,
    accessArea: 'modelBrowser',
    accessRequired: 'write',
  },
  '/spooling': {
    Component: SpoolingPage,
    accessArea: 'spooling',
    accessRequired: 'write',
  },

  // Production
  '/work-orders': {
    Component: WorkOrdersPage,
    mobile: mobileRoute({ Component: MobileWorkOrdersPage }),
    accessArea: 'workOrders',
    accessRequired: 'read',
  },
  '/work-orders/:workOrderId': {
    Component: WorkOrderDetailPage,
    mobile: mobileRoute({
      Layout: WorkOrderDetailWrapper,
      Component: MobileWorkOrderDetailPage,
      subRoutes: {
        '/details': MobileWorkOrderDetailsView,
        '/attachments': MobileWorkOrderAttachmentsView,
        '/activity': MobileWorkOrderActivityView,
      },
    }),
    accessArea: 'workOrders',
    accessRequired: 'read',
  },
  '/shipping-orders': {
    Component: ShipmentsPage,
    accessArea: 'shipments',
    accessRequired: 'write',
  },
  '/shipping-orders/:shipmentId': {
    Component: ShipmentDetailPage,
    accessArea: 'shipments',
    accessRequired: 'write',
  },
  '/bill-of-materials': {
    Component: BillOfMaterialsPage,
    accessArea: 'billOfMaterials',
    accessRequired: 'write',
  },
  '/tasks': {
    Component: WorkCellTasksPage,
    accessArea: 'tasks',
    accessRequired: 'write',
  },
  '/task-viewer/:taskId': {
    Component: TaskViewerPage,
    accessArea: 'tasks',
    accessRequired: 'write',
  },
  '/time-tracking': {
    Component: TimeTrackingPage,
    accessArea: 'timeTracking',
    accessRequired: 'write',
  },
  '/fabrication': {
    Component: EvolveFabricationPage,
    accessArea: 'fabrication',
    accessRequired: 'read',
  },
} as const satisfies Record<
  `/${string}`,
  {
    /** The component to render on Desktop. */
    Component: React.FC;
    /** Mobile overrides for this route. When omitted, mobile renders the Desktop `Component`. */
    mobile?: AnyMobileRouteConfig;
    /** One of {@link AccessArea} to restrict access to this route. */
    accessArea: AccessArea;
    /** The {@link AccessLevel} required to view this page. */
    accessRequired: AccessLevel;
  }
>;

type RouteHasRouteParam = Extract<keyof typeof ALL_MODULE_ROUTES, `${string}:${string}`>;

/** Mapping from base route to type-safe route. */
type RouteParamMapping = {
  '/analytics/:chartId': `/analytics/${ChartRoute}`;
  '/materials-analytics/:chartId': `/materials-analytics/${MaterialAnalyticsChartId}`;
  '/projects/:tab': `/projects/${ProjectPropertiesTab}`;
  '/task-viewer/:taskId': `/task-viewer/${TaskId}`;
  '/shipping-orders/:shipmentId': `/shipping-orders/${ShipmentId}`;
  '/work-orders/:workOrderId': `/work-orders/${WorkOrderId}`;
  '/qr-code/:qrCodeId': `/qr-code/${QrCodeId}`;
  '/work-cell-setup/:facilityId': `/work-cell-setup/${FacilityId}`;
  '/add-catalog-item/:partCatalogId': `/add-catalog-item/${PartCatalogId}`;
  '/edit-catalog-item/:partId': `/edit-catalog-item/${PartId}`;
};
/**
 * Used as a Type Guard to verify RouteParamMapping contains all required keys.
 * Will throw a tsc error if there is a problem with RouteParamMapping.
 */
type RouteParamMappingVerified = { [K in RouteHasRouteParam]: RouteParamMapping[K] };

/**
 * Extends `Base` (a route's navigable URL type) with any mobile `subRoutes` paths declared on
 * route `K` in `ALL_MODULE_ROUTES` — e.g. a `subRoutes: { '/details': ... }` entry makes
 * `${Base}/details` navigable. Derived from the config, so there is no second list to maintain.
 */
type WithMobileSubRoutes<K extends keyof typeof ALL_MODULE_ROUTES, Base extends string> =
  | Base
  | ((typeof ALL_MODULE_ROUTES)[K] extends { mobile: { subRoutes: infer S } } ? `${Base}${keyof S & string}` : never);

type NonParamRoute = Exclude<keyof typeof ALL_MODULE_ROUTES, RouteHasRouteParam>;

type BaseShopNavigationRoute =
  | { [K in NonParamRoute]: WithMobileSubRoutes<K, K> }[NonParamRoute]
  | { [K in RouteHasRouteParam]: WithMobileSubRoutes<K, RouteParamMappingVerified[K]> }[RouteHasRouteParam];

export type ShopNavigationRoute = BaseShopNavigationRoute | `${BaseShopNavigationRoute}?${string}`;

const defaultRoutes = {
  'Shop.FullAccess': '/work-orders',
  'Shop.LimitedAccess': '/work-orders',
  'Shop.ShopFloor': '/tasks',
  'Evolve.MarketingManager': '/evolve-customers',
  'Company.Administrator': '/analytics',
} as const satisfies Record<ShopRole, ShopNavigationRoute>;
const rolePriority = strongKeys(defaultRoutes);

export const ModuleRoutes = () => {
  const { inMessageCenter } = useMessages();
  const canAccessRoute = useCanAccessRoute();
  const { roles } = useUser();
  const isMobile = useIsMobile();

  const accessibleRoutes = useMemo(
    () => strongEntries(ALL_MODULE_ROUTES).filter(([route]) => canAccessRoute(route)),
    [canAccessRoute],
  );

  const defaultRoute = useMemo(() => {
    const reachableRoutes = isMobile ? accessibleRoutes.filter(([, config]) => 'mobile' in config) : accessibleRoutes;
    if (!hasLengthAtLeast(reachableRoutes, 1)) return '/';

    const sortedRoles = roles
      .filter((r) => rolePriority.includes(r))
      .toSorted((a, b) => rolePriority.indexOf(a) - rolePriority.indexOf(b));
    const priorityRole = sortedRoles[0];
    const priorityRoute = isNotNil(priorityRole) ? defaultRoutes[priorityRole] : undefined;
    return isNotNil(priorityRoute) && reachableRoutes.some(([route]) => route === priorityRoute)
      ? priorityRoute
      : reachableRoutes[0][0];
  }, [accessibleRoutes, isMobile, roles]);

  if (
    accessibleRoutes.length === 0 &&
    // All users should have access to the Messages page,
    // even if they have access to no modules or licenses.
    !inMessageCenter
  ) {
    return <Navigate to="/no-license" />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to={`/${moduleRoute}${defaultRoute}`} replace />} />
      {accessibleRoutes.map(([route, routeConfig]) => {
        // Widen to AnyMobileRouteConfig to render any entry's config the same way, whatever its `TContext`.
        // The type-checking on `TContext` and the various components is done as part of `ALL_MODULE_ROUTES`.
        const mobile: AnyMobileRouteConfig | undefined = 'mobile' in routeConfig ? routeConfig.mobile : undefined;
        if (isMobile && isNotNil(mobile)) {
          const { Component: MobileComponent, Layout: MobileLayout, subRoutes } = mobile;
          if (isNotNil(MobileLayout)) {
            return (
              <Route
                key={route}
                path={route}
                element={<MobileLayout>{(context) => <Outlet context={context} />}</MobileLayout>}
              >
                <Route index element={<OutletContextPage Page={MobileComponent} />} />
                {isNotNil(subRoutes) &&
                  strongEntries(subRoutes).map(([subRoute, SubRoutePage]) => (
                    <Route
                      key={subRoute}
                      path={subRoute.replace(/^\//, '')}
                      element={<OutletContextPage Page={SubRoutePage} />}
                    />
                  ))}
              </Route>
            );
          }
          return <Route key={route} path={route} element={<MobileComponent />} />;
        }
        // On desktop, a route with mobile sub-pages accepts any sub-path,
        // so deep links shared from mobile degrade to the full desktop page.
        const path = isNotNil(mobile?.subRoutes) ? `${route}/*` : route;
        return (
          <Route
            key={route}
            path={path}
            element={
              <MobileUnsupportedGate>
                <DesktopLayoutCard>
                  <routeConfig.Component />
                </DesktopLayoutCard>
                <RouteBasedWelcomeModal route={route} />
              </MobileUnsupportedGate>
            }
          />
        );
      })}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};
